/**
 * Image de partage : la grille des retouches composee dans un canvas offscreen,
 * avec un bandeau discret nom + URL.
 *
 * Le PNG n'est JAMAIS televerse : il est genere localement puis passe soit a
 * navigator.share, soit a un telechargement. Rien ne sort du navigateur.
 */
import { SHARE_MAX_ARTWORKS } from '@retusch/shared';
import { SHARE_URL } from '../config.js';
import { prepareArtwork, type ArtworkRenderInput } from './renderArtwork.js';

const WIDTH = 1200;
const PADDING = 32;
const GAP = 16;
const FOOTER_HEIGHT = 72;

/** Repris de tokens.css : l'image doit ressembler au jeu. */
const BG = '#101014';
const FRAME = '#0a0a0d';
const BORDER = '#ffffff29';
const GOLD = '#e8b84b';
const TEXT_2 = '#a8a6a0';

function columnsFor(count: number): number {
  if (count <= 1) return 1;
  if (count <= 4) return 2;
  return 3;
}

export interface ShareImageOptions {
  readonly title: string;
  readonly items: readonly ArtworkRenderInput[];
}

/** Compose l'image et la rend en blob PNG. */
export async function buildShareImage(options: ShareImageOptions): Promise<Blob> {
  const items = options.items.slice(0, SHARE_MAX_ARTWORKS);
  const columns = columnsFor(items.length);
  const rows = Math.max(1, Math.ceil(items.length / columns));

  const cell = Math.floor((WIDTH - PADDING * 2 - GAP * (columns - 1)) / columns);
  const height = PADDING * 2 + rows * cell + GAP * (rows - 1) + FOOTER_HEIGHT;

  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('contexte 2d indisponible');

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, WIDTH, height);

  // Les tableaux sont rendus un par un dans un canvas intermediaire a la taille
  // de la cellule, puis reportes : meme chemin de rendu que les miniatures.
  const tile = document.createElement('canvas');
  for (const [index, input] of items.entries()) {
    const column = index % columns;
    const row = Math.floor(index / columns);
    const x = PADDING + column * (cell + GAP);
    const y = PADDING + row * (cell + GAP);

    try {
      const prepared = await prepareArtwork(input);
      prepared.paint(tile, { size: cell, badge: true });
      ctx.drawImage(tile, x, y);
    } catch {
      ctx.fillStyle = FRAME;
      ctx.fillRect(x, y, cell, cell);
    }

    ctx.strokeStyle = BORDER;
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, cell - 1, cell - 1);
  }

  // Bandeau bas : discret, une seule ligne.
  const footerY = height - FOOTER_HEIGHT;
  ctx.fillStyle = FRAME;
  ctx.fillRect(0, footerY, WIDTH, FOOTER_HEIGHT);
  ctx.strokeStyle = BORDER;
  ctx.beginPath();
  ctx.moveTo(0, footerY + 0.5);
  ctx.lineTo(WIDTH, footerY + 0.5);
  ctx.stroke();

  ctx.textBaseline = 'middle';
  ctx.fillStyle = GOLD;
  ctx.font = '600 26px Fraunces, Georgia, serif';
  ctx.fillText(options.title, PADDING, footerY + FOOTER_HEIGHT / 2);

  ctx.fillStyle = TEXT_2;
  ctx.font = '400 20px "JetBrains Mono", ui-monospace, monospace';
  ctx.textAlign = 'right';
  ctx.fillText(SHARE_URL.replace(/^https?:\/\//, ''), WIDTH - PADDING, footerY + FOOTER_HEIGHT / 2);
  ctx.textAlign = 'left';

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob === null) reject(new Error('encodage PNG impossible'));
      else resolve(blob);
    }, 'image/png');
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Laisse au navigateur le temps de demarrer le telechargement.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Partage natif si le navigateur sait partager un fichier, sinon
 * telechargement. Retourne ce qui a reellement eu lieu.
 */
export async function shareOrDownload(blob: Blob, text: string): Promise<'shared' | 'downloaded'> {
  const file = new File([blob], 'retusch.png', { type: 'image/png' });

  const nav = navigator as Navigator & {
    canShare?: (data: ShareData) => boolean;
    share?: (data: ShareData) => Promise<void>;
  };

  if (typeof nav.share === 'function' && nav.canShare?.({ files: [file] }) === true) {
    try {
      await nav.share({ files: [file], text, url: SHARE_URL });
      return 'shared';
    } catch {
      // Partage annule ou refuse : on retombe sur le telechargement.
    }
  }

  downloadBlob(blob, 'retusch.png');
  return 'downloaded';
}

export interface SocialIntent {
  readonly id: 'x' | 'reddit' | 'discord';
  readonly label: string;
  readonly url: string;
}

/**
 * Intents sociaux. Discord n'a pas d'intent de publication : le lien ouvre
 * l'app, le texte est copie a cote par l'appelant.
 */
/**
 * Liens de partage desktop.
 *
 * Discord n'y figure pas : son lien ne pouvait mener qu'a discord.com/app, sans
 * emporter ni le texte ni l'image. Il donnait l'illusion d'un partage qui
 * n'existait pas. Sur Discord, on colle l'image telechargee.
 *
 * L'URL partagee est celle du JEU, pas celle d'une partie : rien n'est televerse
 * nulle part, l'image voyage comme fichier depuis le navigateur.
 */
/**
 * Liens de partage du chemin desktop.
 *
 * `text` ne contient JAMAIS l'URL du jeu : chaque destination la recoit dans
 * son propre parametre et l'accole elle-meme. La mettre aussi dans le texte la
 * ferait apparaitre deux fois, ce qui s'est deja produit.
 */
export function socialIntents(text: string): readonly SocialIntent[] {
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(SHARE_URL);
  return [
    {
      id: 'x',
      label: 'X',
      url: `https://x.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    },
    {
      id: 'reddit',
      label: 'Reddit',
      url: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedText}`,
    },
  ];
}
